import React from 'react';
import { motion } from 'framer-motion';
import { Target, Settings, Rocket } from 'lucide-react';

const steps = [
  {
    number: 1,
    icon: Target,
    title: 'Discovery',
    description: 'Understand your investment thesis, capital deployment goals, and what success looks like for you',
  },
  {
    number: 2,
    icon: Settings,
    title: 'Calibration',
    description: 'Configure our sourcing systems and algorithms to align precisely with your requirements',
  },
  {
    number: 3,
    icon: Rocket,
    title: 'Execution',
    description: 'Proactive deal flow, structured opportunities, and partnership support through every transaction',
  },
];

export const QualificationProcess: React.FC = () => {
  return (
    <section className="py-16 sm:py-20 lg:py-24 relative">
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 bg-luxury-gold/10 border border-luxury-gold/20 rounded-full mb-4"
          >
            <span className="text-sm font-medium text-luxury-gold">How It Works</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-luxury-text dark:text-luxury-text-dark"
          >
            From Discovery to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold to-luxury-gold-light">
              Deal Flow
            </span>
          </motion.h2>
        </div>

        {/* Steps Grid */}
        <div className="relative">
          {/* Connecting Line (desktop only) */}
          <div className="hidden md:block absolute top-16 left-[16.67%] right-[16.67%] h-0.5 bg-gradient-to-r from-luxury-gold/20 via-luxury-gold/40 to-luxury-gold/20" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15 }}
                  className="relative flex flex-col items-center text-center"
                >
                  {/* Step Number & Icon Container */}
                  <div className="relative mb-6">
                    {/* Icon Circle */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-luxury-surface dark:bg-luxury-elevated border border-luxury-gold/20 flex items-center justify-center shadow-lg group hover:border-luxury-gold/40 transition-all duration-300">
                      <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-luxury-gold group-hover:scale-110 transition-transform duration-300" />
                    </div>

                    {/* Number Badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-luxury-gold to-luxury-gold-light flex items-center justify-center text-luxury-text font-bold text-sm shadow-lg border-2 border-white dark:border-luxury-bg-dark">
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg sm:text-xl font-semibold text-luxury-text dark:text-luxury-text-dark mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-luxury-text-muted dark:text-luxury-text-muted-dark leading-relaxed max-w-xs">
                    {step.description}
                  </p>

                  {/* Mobile Connector */}
                  {idx < steps.length - 1 && (
                    <div className="md:hidden w-0.5 h-8 bg-gradient-to-b from-luxury-gold/40 to-luxury-gold/10 mt-6" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QualificationProcess;
